/**
 * Session Expiry Toast Component
 * 
 * Sprint 2025-11-012: Session expiry UX enhancement
 * 
 * Displays warning toast when session has less than 5 minutes remaining.
 * Provides options to extend session or logout immediately.
 * 
 * Features:
 * - Countdown timer (updates every second)
 * - "Extend Session" button (resets timeout)
 * - "Logout Now" button (explicit logout)
 * - Auto-dismisses on session extended or logout
 * 
 * Maritime Context:
 * - Officers get advance warning before interruption
 * - Can extend session without losing work context
 * - Graceful UX for critical maritime operations
 */

import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Clock, AlertCircle } from 'lucide-react';
import { encryptedStorage } from '@/lib/auth/encryptedStorage';

interface SessionExpiryToastProps {
  onExtend: () => void;
  onLogout: () => void;
  isVisible: boolean;
}

/**
 * Format milliseconds as MM:SS
 */
function formatTimeRemaining(ms: number): string {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

/**
 * Session Expiry Toast Component
 * 
 * This component doesn't render anything directly - it manages the Sonner toast
 */
export function SessionExpiryToast({ onExtend, onLogout, isVisible }: SessionExpiryToastProps) {
  const [toastId, setToastId] = useState<string | number | null>(null);
  const [timeRemaining, setTimeRemaining] = useState<number>(0);

  useEffect(() => {
    if (!isVisible) {
      // Dismiss toast if visible flag changes
      if (toastId !== null) {
        toast.dismiss(toastId);
        setToastId(null);
      }
      return;
    }

    // Update time remaining every second
    const updateInterval = setInterval(() => {
      const remaining = encryptedStorage.getTimeUntilExpiry();
      if (remaining === null || remaining <= 0) {
        // Session expired or doesn't exist
        if (toastId !== null) {
          toast.dismiss(toastId);
          setToastId(null);
        }
        return;
      }
      setTimeRemaining(remaining);
    }, 1000);

    // Show toast if not already shown
    if (toastId === null) {
      const id = toast(
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="font-semibold text-gray-900">Session Expiring Soon</p>
            <p className="text-sm text-gray-600 mt-1">
              Your session will expire in <span className="font-mono font-semibold text-amber-600">{formatTimeRemaining(timeRemaining)}</span>
            </p>
            <div className="flex gap-2 mt-3">
              <button
                onClick={() => {
                  onExtend();
                  if (id) toast.dismiss(id);
                }}
                className="px-3 py-1.5 bg-primary-600 text-white rounded-md text-sm font-medium hover:bg-primary-700 transition-colors"
              >
                Extend Session
              </button>
              <button
                onClick={() => {
                  onLogout();
                  if (id) toast.dismiss(id);
                }}
                className="px-3 py-1.5 bg-gray-200 text-gray-700 rounded-md text-sm font-medium hover:bg-gray-300 transition-colors"
              >
                Logout Now
              </button>
            </div>
          </div>
        </div>,
        {
          duration: Infinity, // Keep toast visible until user action
          icon: <Clock className="w-5 h-5" />,
        }
      );
      setToastId(id);
    }

    return () => {
      clearInterval(updateInterval);
    };
  }, [isVisible, toastId, timeRemaining, onExtend, onLogout]);

  // This component doesn't render anything
  return null;
}
